import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { stage1Service } from 'services/stage1Service';
import { parseApiError } from 'utils/errorParser';
import { notify } from 'features/ui/uiSlice';
import { MESSAGES } from 'constants/messages';

export const fetchStage1 = createAsyncThunk('stage1/fetch', async (params, { rejectWithValue }) => {
  try {
    return await stage1Service.list(params);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const approveStage1 = createAsyncThunk('stage1/approve', async ({ id, documentVersion }, { dispatch, rejectWithValue }) => {
  try {
    const updated = await stage1Service.approve(id, documentVersion);
    dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.APPROVED }));
    return updated;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const rejectStage1 = createAsyncThunk('stage1/reject', async ({ id, reason, documentVersion }, { dispatch, rejectWithValue }) => {
  try {
    const updated = await stage1Service.reject(id, reason, documentVersion);
    dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.REJECTED }));
    return updated;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const bulkApproveStage1 = createAsyncThunk('stage1/bulkApprove', async (ids, { dispatch, rejectWithValue }) => {
  try {
    const response = await stage1Service.bulkApprove(ids);
    dispatch(notify({ type: 'success', message: response.message }));
    return response;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const bulkRejectStage1 = createAsyncThunk('stage1/bulkReject', async (ids, { dispatch, rejectWithValue }) => {
  try {
    const response = await stage1Service.bulkReject(ids);
    dispatch(notify({ type: 'success', message: response.message }));
    return response;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const manualUploadSequential = createAsyncThunk('stage1/manualUploadSequential', async (payload, { dispatch, rejectWithValue }) => {
  try {
    const { newspaperData, articles, isDuplicate } = payload;
    
    // Step 1: Upload the main newspaper PDF to OCR Storage (only if not duplicate)
    if (!isDuplicate) {
      const { ocrStorageService } = await import('services');
      await ocrStorageService.manualUploadOCR(newspaperData);
    }
    
    // Step 2: Upload each article sequentially
    const results = [];
    for (let i = 0; i < articles.length; i++) {
      const articleForm = articles[i];
      const created = await stage1Service.manualUpload(articleForm);
      results.push(created);
    }
    
    let successMsg = '';
    if (isDuplicate) {
      successMsg = 'Successfully uploaded articles.';
    } else if (articles.length > 0) {
      successMsg = 'Successfully uploaded newspaper and articles.';
    } else {
      successMsg = 'Successfully uploaded newspaper.';
    }
    
    dispatch(notify({ type: 'success', message: successMsg }));
    return results;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

const stage1Slice = createSlice({
  name: 'stage1',
  initialState: {
    items: [],
    meta: { total: 0, page: 1, pageSize: 12, totalPages: 0 },
    status: 'idle',
    saving: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStage1.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchStage1.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { items, total, page, pageSize, totalPages } = action.payload;
        state.items = items;
        state.meta = { total, page, pageSize, totalPages };
      })
      .addCase(fetchStage1.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload?.message; })
      .addCase(approveStage1.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(rejectStage1.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(bulkApproveStage1.pending, (state) => { state.saving = true; })
      .addCase(bulkApproveStage1.fulfilled, (state) => { state.saving = false; })
      .addCase(bulkApproveStage1.rejected, (state, action) => { state.saving = false; state.error = action.payload?.message; })
      .addCase(bulkRejectStage1.pending, (state) => { state.saving = true; })
      .addCase(bulkRejectStage1.fulfilled, (state) => { state.saving = false; })
      .addCase(bulkRejectStage1.rejected, (state, action) => { state.saving = false; state.error = action.payload?.message; })
      .addCase(manualUploadSequential.pending, (state) => { state.saving = true; })
      .addCase(manualUploadSequential.fulfilled, (state) => { state.saving = false; })
      .addCase(manualUploadSequential.rejected, (state, action) => { state.saving = false; state.error = action.payload?.message; });
  },
});

export default stage1Slice.reducer;
